import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DateTools } from '@treezor/design-system';

import { IPeriod, IPeriodField, periodBases } from '@/ui-kit/table-filters/filters.type';
import { isBetween, isFrom, isTo } from '@/ui-kit/table-filters/PeriodFilter';
import { ICardTransactionFilterFormFields } from '@/store/types/card-transactions.type';

function getQueryDateTime(dateString: string) {
  const date = new Date(dateString);
  date.setHours(date.getHours() - 1);

  return DateTools.format(date, 'iso');
}

export function mapCardTransactionDate(value) {
  if (isFrom(value) && value.base === periodBases.ON) {
    const selectedDate = getQueryDateTime(value.from);
    return {
      createdDateFrom: selectedDate,
      createdDateTo: selectedDate,
    };
  } else if (isFrom(value) && value.base === periodBases.AFTER) {
    return { createdDateFrom: getQueryDateTime(value.from), createdDateTo: DateTools.format(new Date(), 'iso') };
  } else if (isTo(value)) {
    return { createdDateTo: getQueryDateTime(value.to) };
  } else if (isBetween(value)) {
    return {
      createdDateFrom: getQueryDateTime(value.from),
      createdDateTo: getQueryDateTime(value.to),
    };
  }
}

export function useCardTransactionDateField(
  methods: UseFormReturn<ICardTransactionFilterFormFields>,
  onChange,
): IPeriodField {
  const handleDateChange = useCallback(
    (value: IPeriod) => {
      if (value.base === null && value.from === null && value.to === null) {
        methods.setValue('createdDate', value);
        onChange();
        return;
      }

      const currentValue = methods.getValues('createdDate');
      const newValue = Object.keys(value).reduce<IPeriod>((prev, current) => {
        return { ...prev, [current]: value[current] === null ? currentValue[current] : value[current] };
      }, {} as IPeriod);
      methods.setValue('createdDate', newValue);

      if (isFrom(newValue) || isTo(newValue) || isBetween(newValue)) {
        onChange();
      }
    },
    [onChange, methods],
  );

  const dateValue = methods.watch('createdDate');

  return { value: dateValue, handleChange: handleDateChange };
}
